import { z } from 'zod'
import { isoDate, isoDateTime, uuid } from './common.js'
import { planDay, planLoose } from './items.js'
import { participantIdentity } from './participants.js'
import { tripStatus } from './trips.js'

/**
 * The path a traveller's link takes. This shape is a permanent contract: a
 * bookmarked URL must still resolve after a domain move, a rewrite, or a
 * migration off this codebase entirely. Change the host all you like; never
 * change this.
 */
export const SHARE_PATH_PREFIX = '/s/'

export const shareToken = z
  .string()
  .regex(/^[A-Za-z0-9_-]{22,64}$/, 'malformed share token')

/** Renaming a link. Blank clears the name rather than storing an empty one. */
export const renameShareLinkRequest = z.object({
  label: z.string().trim().max(120).nullish(),
})
export type RenameShareLinkRequest = z.infer<typeof renameShareLinkRequest>

export const createShareLinkRequest = z.object({
  /**
   * What Ana calls it: "grupo do WhatsApp", "cópia do Carlos".
   *
   * Separate from `participantId`, because the two answer different questions.
   * The binding decides who a comment is attributed to; the label is how she
   * finds the right link among six identical URLs.
   */
  label: z.string().trim().max(120).nullish(),
  /** Bind the link to a named person so their actions can be attributed. */
  participantId: uuid.nullish(),
})
export type CreateShareLinkRequest = z.infer<typeof createShareLinkRequest>

export const shareLink = z.object({
  id: uuid,
  tripId: uuid,
  token: shareToken,
  /**
   * Constructed from PUBLIC_BASE_URL when this response is built — never read
   * from a column. Only the token is stored (security invariant 3), so moving
   * the product to a different domain is a configuration edit, not a data
   * migration, and every link already in a client's hands keeps working.
   */
  url: z.url(),
  participantId: uuid.nullable(),
  label: z.string().nullable(),
  revokedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export type ShareLink = z.infer<typeof shareLink>

/**
 * What an anonymous holder of a link receives. Read-only.
 *
 * This is the payload the offline cache stores, the print route renders and the
 * updates surface diffs against, so its shape is load-bearing well beyond the
 * share page. It is assembled in one read — a traveller on hotel wifi should
 * not be waiting on six round trips.
 */
export const sharePlan = z.object({
  trip: z.object({
    id: uuid,
    title: z.string(),
    startDate: isoDate,
    endDate: isoDate,
    timezone: z.string(),
    status: tripStatus,
    /**
     * Rendered ABOVE the itinerary, always. Somebody reaching for this is
     * having a bad day and should not have to scroll past Tuesday to find it.
     */
    emergencyInfo: z.string().nullable(),
  }),
  agency: z.object({ name: z.string() }),
  /** Null when the advisor issued an unbound link — a plan with no name on it. */
  participant: participantIdentity.nullable(),
  days: z.array(planDay),
  /** On the trip but not on a day. Shown after the days rather than hidden. */
  loose: planLoose,
  /**
   * The DATABASE's clock when this payload was assembled, and the only instant
   * a traveller's "since I last looked" is ever measured from.
   *
   * It has to come from here rather than from the browser, because the two
   * things being compared are a server-stamped `updated_at` and a viewer's idea
   * of when they last looked. A phone ten minutes fast would silently skip
   * every edit Ana made in the last ten minutes, and a phone ten minutes slow
   * would announce the same edits twice — neither with any symptom to notice.
   * Taking both ends of the comparison off one clock removes the class.
   *
   * It is `transaction_timestamp()`, so it is taken BEFORE the plan is read,
   * not after. That direction matters: a row committed while this response was
   * being assembled then still counts as newer than `readAt` and gets reported
   * on the next visit. Stamping it afterwards would let such a row fall into
   * the gap between "not in this payload" and "older than the baseline" and
   * never be mentioned at all.
   */
  readAt: isoDateTime,
})
export type SharePlan = z.infer<typeof sharePlan>
